/*
 * Purpose: Reusable privacy policy modal for tracking and analytics consent disclosure.
 * Boundary: Pure presentational; no business logic, API calls, or state beyond open/close.
 * Invariants: Props { open, onClose }; renders nothing when open=false; closes only via X button.
 */

export default function PrivacyPolicyModal({ open, onClose }) {
    if (!open) return null;

    return (
        <div
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/60'
            role='presentation'
        >
            <div
                className='relative w-full max-w-lg mx-4 bg-neutral-900 border border-neutral-700 rounded-xl shadow-2xl flex flex-col max-h-[80vh]'
                role='dialog'
                aria-label='Privacy Policy'
            >
                {/* Header */}
                <div className='flex items-center justify-between px-5 py-4 border-b border-neutral-700'>
                    <div className='flex items-center gap-2 text-neutral-200'>
                        <svg className='w-5 h-5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                            <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
                        </svg>
                        <span className='font-semibold text-sm md:text-base'>Privacy Policy - Tracking &amp; Analytics</span>
                    </div>
                    <button
                        onClick={onClose}
                        className='text-neutral-400 hover:text-neutral-200 text-xl leading-none px-1'
                        aria-label='Close'
                    >
                        &times;
                    </button>
                </div>

                {/* Body -- scrollable */}
                <div className='flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm text-neutral-300 leading-relaxed'>
                    <h3 className='text-neutral-200 font-semibold text-base'>What we collect</h3>
                    <p>We use Meta's Conversions API to measure the effectiveness of our advertising on the Meta platform (Facebook, Instagram). When you visit our landing page and have consented to tracking, we send the following limited technical data from <strong className='text-neutral-200'>our servers</strong> to Meta Platforms, Inc.:</p>
                    <ul className='list-disc list-inside space-y-1 pl-2'>
                        <li>The URL of the page you visited</li>
                        <li>Your IP address</li>
                        <li>Your browser type (user agent string)</li>
                        <li>The date and time of your visit</li>
                    </ul>
                    <p>This data is sent server-to-server. <strong className='text-neutral-200'>No Meta scripts, pixels, or tracking code runs in your browser.</strong> We do not share your name, identity number, cellphone number, email address, login credentials, or any other personally identifiable information with Meta.</p>

                    <h3 className='text-neutral-200 font-semibold text-base'>How Meta uses this data</h3>
                    <p>Meta uses this data to measure advertising performance (click attribution, conversion tracking) and may link it to a Meta account if one exists for your IP address. For details, see Meta's Privacy Policy.</p>

                    <h3 className='text-neutral-200 font-semibold text-base'>Cross-border transfer</h3>
                    <p>This data is transferred to Meta Platforms, Inc. in the <strong className='text-neutral-200'>United States</strong>, where data protection laws may differ from those in South Africa. By consenting to tracking on our landing page, you consent to this transfer in terms of Section 72(1)(b) of the Protection of Personal Information Act (POPIA).</p>

                    <h3 className='text-neutral-200 font-semibold text-base'>Your rights</h3>
                    <ul className='list-disc list-inside space-y-1 pl-2'>
                        <li>You may <strong className='text-neutral-200'>withdraw consent</strong> at any time by clearing your browser cookies. On your next visit, you will be asked to consent again.</li>
                        <li>You may request access to, correction of, or deletion of your personal information by contacting us.</li>
                        <li>Consent records are retained for <strong className='text-neutral-200'>12 months</strong> from the date of consent for record-keeping purposes.</li>
                    </ul>

                    <h3 className='text-neutral-200 font-semibold text-base'>Consent mechanism</h3>
                    <p>On your first visit to our landing page, you are presented with a choice to accept or reject tracking. If you accept, a secure, tamper-proof cookie is set in your browser to record your decision. If you reject, no data is sent to Meta. Your choice does not affect your ability to use our services.</p>
                </div>

                {/* Footer */}
                <div className='px-5 py-4 border-t border-neutral-700'>
                    <button
                        onClick={onClose}
                        className='w-full px-4 py-2 rounded-lg bg-neutral-800 text-neutral-200 font-medium text-sm hover:bg-neutral-700 transition'
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
